import { unwrap } from "@majourneys/api-client";
import { api } from "../../../lib/api";
import type { CurrencyCode, StayCancellation, StayProperty, StayRate } from "@majourneys/shared";
import { readGclid } from "@majourneys/booking";
import { getStaySessionId } from "../lib/session-id";

/**
 * `/stays/*` is mounted off the main Elysia chain in apps/api
 * (`app.use(staysRoutes())` in apps/api/src/index.ts, alongside addons, ai,
 * social, partners and search-console) to keep the already-large `typeof
 * app` chain under TypeScript's instantiation depth limit (TS2589). Off-chain
 * plugins never appear in the Eden Treaty `App` type, so `api.stays` does not
 * exist at the type level even though the route exists at runtime.
 *
 * This is the same `(apiClient as any)["search-console"]` cast used in
 * apps/admin/src/features/marketing/search-console/api/search-console.api.ts
 * (its own comment references the same stop-sales/reservations-actions
 * precedent). The cast is contained to this one line; every function below
 * returns an explicit type built from `@majourneys/shared` and the response
 * shapes documented in apps/api/src/features/stays/routes/index.ts and
 * stays.catalogue.service.ts / stays.booking.service.ts, so the `any` never
 * leaks into stays-query.ts or any component.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stays = (api as any).stays;

/**
 * The server strips `netTotal`, `sspTotal` and `sspCurrency` off `StayRate`
 * before a rate ever reaches a client (see `PublicStayRate` in
 * apps/api/src/features/stays/stays.service.ts) — those are supplier-net /
 * price-floor fields that must never leave the server. Mirrored here as a
 * type-only `Omit` of the shared `StayRate`, not a redeclaration of it.
 */
export type PublicStayRate = Omit<StayRate, "netTotal" | "sspTotal" | "sspCurrency"> & {
  /** True only on a rate priced for a signed-in member. */
  memberPrice?: true;
  /** What a signed-out visitor pays for the same rate. Member rates only. */
  publicSellTotal?: number | null;
};

/**
 * off: the feature is switched off. available: on, caller signed out.
 * applied: on, caller signed in and priced as a member.
 */
export type MemberPricesState = "off" | "available" | "applied";

export interface StayDestination {
  id: string;
  /** Nullable on the cities table; a city without a slug has no public page. */
  slug: string | null;
  name: string;
  /** Published properties in this city. */
  propertyCount?: number;
  /** A photo from the city's best-ranked property, for the destination card. */
  heroImage?: string | null;
}

export interface StaysDestinationsResponse {
  destinations: StayDestination[];
}

export interface StaysAllResponse {
  properties: StaysCityResponse["properties"];
  pricingUnavailable: boolean;
}

export interface StaysCityResponse {
  city: StayDestination;
  properties: (StayProperty & { price: PublicStayRate | null })[];
  pricingUnavailable: boolean;
}

export interface StaysPropertyResponse {
  property: StayProperty;
  rooms: PublicStayRate[];
  pricingUnavailable: boolean;
  /** Absent on an older API; treat as "off". */
  memberPrices?: MemberPricesState;
}

/**
 * Guest reviews for one property, from `GET /stays/property/:slug/reviews`.
 * Mirrors `StayReviewsResponse` in apps/api/src/features/stays/stays.reviews.ts.
 *
 * `rating` and category ratings are on the supplier's 0 to 10 scale. Category
 * names are the supplier's English words; translate the known ones on the
 * page. A supplier failure arrives as an empty list, never as an error.
 */
export interface StayReviewsResponse {
  rating: number | null;
  reviewCount: number;
  /** First page only. */
  summary: { categories: { name: string; rating: number }[]; pros: string[] } | null;
  reviews: {
    id: string;
    /** First name only. */
    name: string;
    score: number;
    date: string;
    tripType: string | null;
    /** The guest's own title; null when they left none. */
    title: string | null;
    /** May be empty when the guest left only a title. */
    text: string;
    language: string | null;
    /** ISO alpha-2, upper case, e.g. "FR". */
    country: string | null;
  }[];
  total: number;
  offset: number;
  /** Where the next page starts in the supplier's list; null at the end. */
  nextOffset?: number | null;
}

/**
 * The supplier re-priced (or changed cancellation/board terms) between the
 * price the guest saw and the moment the rate was locked — no booking is
 * created and no payment token is issued; the guest must see the diff and
 * confirm again.
 */
export interface StartBookingReconfirmation {
  requiresReconfirmation: true;
  newPrice: number;
  currency: string;
  priceDifferencePercent: number;
  cancellationChanged: boolean;
  boardChanged: boolean;
}

export interface StartBookingConfirmation {
  requiresReconfirmation: false;
  reference: string;
  /**
   * When the held price is assumed to die. Our own deadline, not the
   * supplier's — a prebook response carries no expiry at all — so it is only
   * ever used to warn the guest and to refuse STARTING a payment, never to
   * refuse finishing one already paid for.
   */
  expiresAtIso: string | null;
  secretKey: string;
  transactionId: string;
  sellTotal: number;
  currency: string;
  taxesAtPropertyTotal: number;
  cancellation: StayCancellation;
}

export type StartBookingResult = StartBookingReconfirmation | StartBookingConfirmation;

export interface StayBookingHolderInput {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

export interface StartBookingInput {
  propertySlug: string;
  checkin: string;
  checkout: string;
  adults: number;
  offerId: string;
  /**
   * Locale the guest is booking in, stored on the row so their confirmation
   * and any failure email arrive in it. Without this every stays email went
   * out in English while the templates ship all seven locales.
   */
  lang?: string;
  /** Carried so the booking records the party, not just the adults. */
  childAges?: number[];
  /** Google Ads click id; filled in by `startBooking` itself. */
  gclid?: string;
  holder: StayBookingHolderInput;
}

/**
 * Field allow-list matching `serialiseStayBooking` in
 * apps/api/src/features/stays/routes/index.ts — the underlying row also
 * carries `netTotal`, `prebookId`, `transactionId` (post-finalise) and
 * `marginPct`, none of which the API returns from these endpoints.
 */
export interface StayBooking {
  reference: string;
  /** Which hotel this booking is for. The server joins it; a booking row alone
   *  only knows a room type, which is not enough for a confirmation. */
  propertyName: string | null;
  propertySlug: string | null;
  propertyCitySlug: string | null;
  status: string;
  checkIn: string;
  checkOut: string;
  adults: number;
  /** One age per child on this booking. Empty for adults only. */
  childAges: number[];
  rooms: number;
  roomName: string;
  boardName: string | null;
  currency: string;
  sellTotal: number;
  taxesAtPropertyTotal: number;
  cancellation: StayCancellation;
  holderFirstName: string;
  holderLastName: string;
  holderEmail: string;
  holderPhone: string | null;
  createdAt: string;
}

export interface CancellationQuote {
  refundAmount: number;
  chargeAmount: number;
  currency: string;
  refundable: boolean;
  freeUntilIso: string | null;
}

export interface StayPricingParams {
  checkin?: string;
  checkout?: string;
  adults?: number;
  /** One age per child. Empty or absent means an adults-only search. */
  childAges?: number[];
  /** Narrow a city list to one stay type. Set by /category/<type>/<city>. */
  propertyType?: string;
  /**
   * The currency the guest picked in the header. Forwarded to the supplier so
   * the quote arrives denominated in it — a stay is charged online in full, so
   * converting a MAD quote in the browser would print a number the card
   * statement never matches.
   *
   * Absent means MAD, which is what the API defaults to.
   */
  currency?: CurrencyCode;
  /**
   * The visitor's country (ISO-3166 alpha-2), forwarded so the supplier
   * quotes the market the guest is actually in. Absent means MA, which is
   * what the API defaults to and what every search sent before this existed.
   */
  guestNationality?: string;
}

type StayPricingQuery = {
  /** Opaque per-tab id; keeps the supplier's price steady through checkout. */
  sessionId?: string;
  checkin: string;
  checkout: string;
  adults: number;
  childAges?: string;
  currency?: string;
  guestNationality?: string;
};

/**
 * The backend only prices when `checkin`, `checkout` and `adults` are ALL
 * present (see `stayPropertyListQuerySchema`'s refine in
 * packages/shared/src/schemas/stay-properties.ts) — a partial set doesn't
 * error, it just silently comes back with `pricingUnavailable` content only.
 * So date/occupancy params are only ever sent as a complete set.
 */
export function buildStayPricingQuery(params: StayPricingParams): StayPricingQuery | Record<string, never> {
  const { checkin, checkout, adults, childAges, currency, guestNationality } = params;
  const sessionId = getStaySessionId();
  if (checkin && checkout && adults != null) {
    return {
      checkin,
      checkout,
      adults,
      /**
       * Only sent alongside a full date set, and only when it is not the
       * default. Undated requests never reach the supplier, so a currency on
       * one would change nothing but the URL — and those are precisely the
       * crawlable, edge-cached responses whose cache key we do not want to
       * fragment four ways for no difference in the body.
       */
      ...(currency && currency !== "MAD" ? { currency } : {}),
      /**
       * Same rule as `currency`, for the same reason: only alongside a full
       * date set, because an undated request never reaches the supplier, and
       * those are the crawlable, edge-cached responses whose cache key must
       * not fragment by market for a byte-identical body. `useGuestNationality`
       * has already dropped MA, so anything here is a real difference.
       */
      ...(guestNationality ? { guestNationality } : {}),
      // Only sent when there are children: an empty param would read as a
      // deliberate "no children" where absent means the same thing more
      // cheaply, and keeps adults-only URLs unchanged.
      ...(childAges && childAges.length > 0 ? { childAges: childAges.join(",") } : {}),
      // Keeps the supplier quoting this guest the same price from search
      // through to checkout. Absent in SSR and wherever storage is blocked,
      // which is the pre-existing behaviour.
      ...(sessionId ? { sessionId } : {}),
    };
  }
  return {};
}

/** What the server remembers about an offer it already served. */
export interface ServedOffer {
  propertySlug: string;
  supplierHotelId: string;
  room: PublicStayRate;
  checkin: string;
  checkout: string;
  adults: number;
  childAges: number[];
}

export interface StayCalendarNight {
  date: string;
  available: boolean;
  /** Indicative one-night price, margin applied. Null when unknown. */
  nightlyFrom: number | null;
  currency: string | null;
}

export interface StayCalendarResponse {
  nights: StayCalendarNight[];
  /** Nights we could not ask about. Left selectable — see the API's builder. */
  failed: string[];
}

export const staysApi = {
  /**
   * Cities that have stays. `propertyType` narrows it to cities holding that
   * kind, which is what the "Filter by city" row needs: without it the row
   * offers cities that have no riad on the riad page.
   */
  destinations: async (propertyType?: string, lang?: string): Promise<StaysDestinationsResponse> =>
    unwrap(
      await stays.destinations.get(
        propertyType || lang
          ? {
              query: {
                ...(propertyType ? { type: propertyType } : {}),
                // City names in the guest's language ("Tangeri", not "Tangier").
                ...(lang && lang !== "en" ? { lang } : {}),
              },
            }
          : undefined,
      ),
    ) as StaysDestinationsResponse,

  /**
   * Every published property in one request (the Stays hub's "All" and the
   * per-type pages). Replaced "which cities?" then one request per city.
   */
  all: async (
    lang: string,
    pricing: StayPricingParams = {},
    /** See `property`: a viewer-null cache entry must be fetched without the cookie. */
    { anonymous = false }: { anonymous?: boolean } = {},
  ): Promise<StaysAllResponse> =>
    unwrap(
      await stays.all.get({
        query: {
          lang,
          ...(pricing.propertyType ? { propertyType: pricing.propertyType } : {}),
          ...buildStayPricingQuery(pricing),
        },
        ...(anonymous ? { fetch: { credentials: "omit" } } : {}),
      }),
    ) as StaysAllResponse,

  city: async (
    citySlug: string,
    lang: string,
    pricing: StayPricingParams = {},
    /** See `property`: a viewer-null cache entry must be fetched without the cookie. */
    { anonymous = false }: { anonymous?: boolean } = {},
  ): Promise<StaysCityResponse> =>
    unwrap(
      await stays.city({ citySlug }).get({
        query: {
          lang,
          // Sent independently of the date set: a type filter is a content
          // choice, not a pricing one, and must work with no dates chosen.
          ...(pricing.propertyType ? { propertyType: pricing.propertyType } : {}),
          ...buildStayPricingQuery(pricing),
        },
        ...(anonymous ? { fetch: { credentials: "omit" } } : {}),
      }),
    ) as StaysCityResponse,

  property: async (
    slug: string,
    lang: string,
    pricing: StayPricingParams = {},
    /**
     * Send no cookie, so the API prices the rooms as it would for anyone.
     * Required for every fetch stored under a viewer-null query key: the
     * browser would otherwise attach a member's session, and member prices
     * would sit in the public entry, shown after sign-out and refused at
     * checkout.
     */
    { anonymous = false }: { anonymous?: boolean } = {},
  ): Promise<StaysPropertyResponse> =>
    unwrap(
      await stays.property({ slug }).get({
        query: { lang, ...buildStayPricingQuery(pricing) },
        ...(anonymous ? { fetch: { credentials: "omit" } } : {}),
      }),
    ) as StaysPropertyResponse,

  /**
   * Which nights this property can actually be booked on, asked live.
   *
   * Replaces reading a nightly catalogue-wide sweep that never finished and
   * was routinely days stale, which is what let a guest pick dates the
   * calendar had offered and then be told there were no rooms.
   */
  propertyCalendar: async (
    slug: string,
    options: { adults?: number; currency?: string } = {},
  ): Promise<StayCalendarResponse> =>
    unwrap(
      await (stays.property({ slug }) as never as {
        calendar: {
          get: (o: unknown) => Promise<{ data: StayCalendarResponse | null; error: unknown }>;
        };
      }).calendar.get({
        query: {
          ...(options.adults ? { adults: String(options.adults) } : {}),
          ...(options.currency ? { currency: options.currency } : {}),
        },
      }),
    ) as StayCalendarResponse,

  /**
   * One page of guest reviews. `limit` is capped at 24 by the API, and at 10
   * for any `lang` other than en (the supplier translates at most ten).
   */
  reviews: async (
    slug: string,
    { offset = 0, limit = 6, lang = "en" }: { offset?: number; limit?: number; lang?: string } = {},
  ): Promise<StayReviewsResponse> =>
    unwrap(
      await (stays.property({ slug }) as never as {
        reviews: {
          get: (o: unknown) => Promise<{ data: StayReviewsResponse | null; error: unknown }>;
        };
      }).reviews.get({
        query: { offset: String(offset), limit: String(limit), lang },
      }),
    ) as StayReviewsResponse,

  /**
   * The exact offer the guest picked, resolved server side from the id in the
   * checkout URL. Returns null once the offer has aged out, which the page
   * turns into "pick a room again" rather than a dead end.
   */
  servedOffer: async (offerId: string): Promise<ServedOffer | null> => {
    try {
      return unwrap(
        await stays.offers({ offerId: encodeURIComponent(offerId) }).get(),
      ) as ServedOffer;
    } catch {
      return null;
    }
  },

  /**
   * Carries the Google Ads click id, so a confirmed hotel booking can be
   * reported to Google Ads like a reservation is (owner, 2026-10-07).
   * `readGclid` only returns one for a guest who accepted cookies.
   */
  startBooking: async (input: StartBookingInput): Promise<StartBookingResult> => {
    const gclid = input.gclid ?? readGclid();
    return unwrap(
      await stays.bookings.start.post(gclid ? { ...input, gclid } : input),
    ) as StartBookingResult;
  },

  finaliseBooking: async (reference: string): Promise<StayBooking> =>
    unwrap(await stays.bookings({ reference }).finalise.post({})) as StayBooking,

  getBooking: async (reference: string): Promise<StayBooking> =>
    unwrap(await stays.bookings({ reference }).get()) as StayBooking,

  cancelQuote: async (reference: string): Promise<CancellationQuote> =>
    unwrap(await stays.bookings({ reference })["cancel-quote"].get()) as CancellationQuote,

  cancelBooking: async (reference: string, email: string): Promise<CancellationQuote> =>
    unwrap(await stays.bookings({ reference }).cancel.post({ email })) as CancellationQuote,
};
